import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, ClipboardList, FileText, PenTool, FolderGit2, ScrollText } from 'lucide-react';
import { specsApi, type SpecSummary, type DocRepos } from '@/services/specsApi';
import { useProject } from '@/contexts/ProjectContext';
import { PublishedBadge } from '@/components/specs/PublishedBadge';
import { PageEmptyState } from '@/components/onboarding/PageEmptyState';
import { CHECKLIST_ACTIONS } from '@/components/onboarding/SetupChecklist';
import { DesignPublishBar } from '@/components/specs/DesignPublishBar';
import { DocListSection, LifecycleBadge, type DocListItem } from '@systemdox/ui';
import { useConsoleUrl } from '@/hooks/useConsoleUrl';
import { docsApi } from '@/services/docsApi';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { DecisionBadge } from '@/components/specs/DecisionBadge';
import {
  decisionReaderPath,
  loadDesignDecisions,
  withoutAuthoredDuplicates,
  type DesignDecision,
} from '@/lib/designDecisions';
import { createLogger } from '@/lib/logger';
import { ChangesEntryCard } from '@/components/changes/ChangesEntryCard';

const log = createLogger('SpecListPage');

/** Pillar-specific copy — same engine, specialised by configuration
 *  (specs/changes/lifecycle-doc-model): Requirements and Design list the
 *  same document store scoped by docType. */
const PILLARS = {
  requirement: {
    title: 'Requirements',
    subtitle: 'Requirement specs following the shared-specs convention (RFC 2119, Given/When/Then).',
    icon: FileText,
    basePath: '/requirements',
    newLabel: 'New Requirement',
    repoField: 'requirementsRepo',
    docsPath: 'docs/requirements/',
    repoNoun: 'requirements repository',
    // Plural noun for the no-match message — derived from title would read
    // ungrammatically for Design ("No design match…").
    emptyNoun: 'requirements',
  },
  design: {
    title: 'Design',
    subtitle: 'The workspace’s decisions: ADRs and design documents read from the design repository, and the ones authored here — linked to the requirements they satisfy.',
    icon: PenTool,
    basePath: '/design',
    newLabel: 'New Design Doc',
    repoField: 'designRepo',
    docsPath: 'docs/design/',
    repoNoun: 'system design repository',
    emptyNoun: 'design docs',
  },
} as const;

export default function SpecListPage({ docType = 'requirement' }: { docType?: keyof typeof PILLARS }) {
  const pillar = PILLARS[docType];
  const { selected: selectedProject } = useProject();
  const navigate = useNavigate();
  // Keep the query string on in-surface navigations — on localhost the
  // ?capture/?tenant params ARE the host selection.
  const { search } = useLocation();
  // Settings links are still built as console URLs — hidden when no slug is
  // resolvable (there is no neutral console host).
  const consoleUrl = useConsoleUrl();
  const [specs, setSpecs] = useState<SpecSummary[]>([]);
  const [docRepos, setDocRepos] = useState<DocRepos | null>(null);
  // Settled once the doc-repo read answered (either way): the Design pillar's
  // git half waits for the design repo's name, not for a null that may mean
  // "still loading".
  const [docReposSettled, setDocReposSettled] = useState(false);
  const [loading, setLoading] = useState(true);
  // The Design pillar's git half (design-pillar-reads-the-decisions): the
  // design repo's indexed ADRs and docs/design/ files. `null` until read, so
  // the list never flashes "no design docs" over a repo holding two hundred.
  const [decisions, setDecisions] = useState<DesignDecision[] | null>(null);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const { allRepos } = useWorkspace();

  useEffect(() => {
    setLoading(true);
    // Server-side type scoping (lifecycle-doc-model R5): the other pillar's
    // docs never appear here. Pre-backfill legacy specs are untyped and
    // absent until backend/scripts/backfill_doc_types.py runs (#536).
    // The selected project scopes the list further (view filter, task 11).
    specsApi.listSpecs({ docType, project: selectedProject?.projectId }).then(setSpecs).finally(() => setLoading(false));
    specsApi.getDocRepos().then(setDocRepos).catch(() => setDocRepos(null)).finally(() => setDocReposSettled(true));
  }, [docType, selectedProject?.projectId]);

  // Clicking a document opens its full editor — which lands in preview mode,
  // so it IS the viewer (specs/changes/consolidated-full-editor R5), and where
  // Publish lives (kb-publish): the rows themselves carry no actions
  // (docs-pillar-parity R5).
  const openDocument = useCallback((spec: SpecSummary) => {
    navigate({ pathname: `${pillar.basePath}/${spec.draftId}`, search });
  }, [navigate, pillar.basePath, search]);

  const repo = docRepos?.[pillar.repoField] || '';

  // list_repos' word on the design repo row: `false` means pushes to it are
  // not indexed (doc_sync_worker, #1350) and the index below is aging.
  const connected = allRepos.find((r) => r.full_name === repo)?.connected;

  const loadDecisions = useCallback(async () => {
    if (docType !== 'design') { setDecisions(null); return; }
    if (!docReposSettled) return;
    if (!repo) { setDecisions([]); return; }
    const [owner, name] = repo.split('/');
    try {
      const [items, sync] = await Promise.all([
        loadDesignDecisions(repo),
        docsApi.getSyncStatus(owner, name).catch(() => null),
      ]);
      setDecisions(items);
      setLastSyncAt(sync?.last_sync_at ?? null);
    } catch (err) {
      log.warn('Design repo index unavailable — listing in-app design docs only', { repo, error: String(err) });
      setDecisions([]);
    }
  }, [docType, docReposSettled, repo]);
  useEffect(() => { void loadDecisions(); }, [loadDecisions]);

  // Sync now: the sync is asynchronous — poll until it settles (the GitHub
  // settings page's cadence), then re-read the list. A refusal (409: the repo
  // is not connected) lands in the log; the bar already says Connect.
  const syncDesignRepo = useCallback(async () => {
    if (!repo) return;
    const [owner, name] = repo.split('/');
    setSyncing(true);
    try {
      await docsApi.syncRepo(owner, name);
      for (let i = 0; i < 30; i += 1) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const status = await docsApi.getSyncStatus(owner, name).catch(() => null);
        if (status && (status.status === 'completed' || status.status === 'failed')) break;
      }
      await loadDecisions();
    } catch (err) {
      log.warn('Design repo sync failed', { repo, error: String(err) });
    } finally {
      setSyncing(false);
    }
  }, [repo, loadDecisions]);
  // A deliberately disabled capability (guided-repo-setup Phase D): existing
  // documents stay browsable, but every "configure this" nag stands down.
  const capabilityKey = docType === 'requirement' ? 'requirements' : 'design';
  const capabilityDisabled = docRepos?.capabilities?.[capabilityKey]?.status === 'disabled';

  // Each spec is a shared listing row (docs-pillar-parity R5): name over the
  // GitHub location, then date + status. Published wins the status slot
  // (kb-publish R6); otherwise the doc's own lifecycle state shows.
  const specItems: DocListItem[] = specs.map((spec) => ({
    id: spec.draftId,
    icon: pillar.icon,
    title: spec.name,
    location: spec.repoFullName || 'No repo',
    date: spec.updatedAt,
    dateLabel: 'Last updated',
    status: spec.publishedAt
      ? <PublishedBadge publishedAt={spec.publishedAt} updatedAt={spec.updatedAt} />
      : <LifecycleBadge status={spec.status} />,
    onOpen: () => openDocument(spec),
    openTitle: 'Open in the full editor',
  }));

  // The design repo's decisions follow the authored documents, newest first,
  // each opening in the book reader (read, edit, listen). A draft already
  // committed into the repo shows once, as the authored row.
  const decisionItems: DocListItem[] = docType === 'design'
    ? withoutAuthoredDuplicates(decisions ?? [], specs).map((d) => ({
        id: `decision:${d.id}`,
        icon: d.kind === 'adr' ? ScrollText : PenTool,
        title: d.title,
        location: `${d.repoFullName} · ${d.path}`,
        date: d.syncedAt,
        dateLabel: 'Indexed',
        status: d.kind === 'adr' ? <DecisionBadge status={d.status} /> : undefined,
        onOpen: () => navigate({ pathname: decisionReaderPath(d), search }),
        openTitle: 'Open in the reader',
      }))
    : [];
  const items: DocListItem[] = [...specItems, ...decisionItems];

  const headerActions = (
    <button
      // Both pillars are authored in the capture Add workflow — a sibling tab
      // on this same surface.
      onClick={() => navigate({ pathname: '/add', search })}
      className="flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
    >
      <Plus className="w-4 h-4" />
      {pillar.newLabel}
    </button>
  );

  // Changes live under Requirements (ADR-218 §2.3): the entry card sits above
  // the repository banner, on this pillar only.
  const changesEntry = docType === 'requirement'
    ? <ChangesEntryCard variant="requirements" className="mb-6" />
    : null;

  const repoBanner = docRepos ? (
    docType === 'design' ? (
      // Design pillar: the source bar — which folders of the design repo the
      // list reads, what the index holds of them and how fresh it is, with a
      // Sync now; or the fact that the repo is not connected.
      <DesignPublishBar
        designRepo={repo}
        capabilityDisabled={capabilityDisabled}
        source={{ decisions, connected, lastSyncAt, syncing, onSync: syncDesignRepo }}
      />
    ) : (
      // Requirements repository banner (#571): published under docs/ at the root.
      // A disabled capability shows the decided fact, never the amber nag.
      capabilityDisabled ? (
        <div className="mb-6 flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 px-4 py-2.5 text-sm text-slate-500 dark:text-slate-400">
          <FolderGit2 className="w-4 h-4 shrink-0 text-slate-400" />
          <span>Requirements publishing is disabled for this workspace.</span>
          {consoleUrl && (
            <a href={consoleUrl('/settings/repo-roles')} className="ml-auto text-blue-600 dark:text-blue-400 hover:underline shrink-0">
              Enable in Settings
            </a>
          )}
        </div>
      ) : repo ? (
        <div className="mb-6 flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 px-4 py-2.5 text-sm text-slate-600 dark:text-slate-400">
          <FolderGit2 className="w-4 h-4 shrink-0 text-slate-400" />
          <span>
            Published to <strong className="text-slate-800 dark:text-slate-200">{repo}</strong>{' '}
            under <code className="px-1 rounded bg-slate-200 dark:bg-slate-800">{pillar.docsPath}</code>
          </span>
          {consoleUrl && (
            <a href={consoleUrl('/settings/repo-roles')} className="ml-auto text-blue-600 dark:text-blue-400 hover:underline shrink-0">
              Change
            </a>
          )}
        </div>
      ) : (
        <div className="mb-6 flex items-center gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-2.5 text-sm text-amber-800 dark:text-amber-200">
          <FolderGit2 className="w-4 h-4 shrink-0" />
          <span>
            No dedicated {pillar.repoNoun} configured yet — SystemDox publishes each pillar to
            its own git repo, under <code className="px-1 rounded bg-amber-100 dark:bg-amber-900/40">docs/</code> at the root.
          </span>
          {consoleUrl && (
            <a href={consoleUrl('/settings/repo-roles')} className="ml-auto font-medium underline hover:no-underline shrink-0">
              Configure
            </a>
          )}
        </div>
      )
    )
  ) : null;

  const banner = (changesEntry || repoBanner) ? (
    <>
      {changesEntry}
      {repoBanner}
    </>
  ) : null;

  const emptyState = docType === 'design' ? (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 text-center">
      <PenTool className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-700" />
      <h2 className="mt-4 font-semibold text-slate-900 dark:text-slate-100">No design docs yet</h2>
      <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
        Design documents capture how requirements are satisfied — architecture
        decision records (ADRs), diagrams, and design notes. Create one and link
        it to the requirements it satisfies.
      </p>
      <button
        onClick={() => navigate({ pathname: '/add', search })}
        className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
      >
        <Plus className="w-3.5 h-3.5" />
        New Design Doc
      </button>
    </div>
  ) : (
    <PageEmptyState
      icon={ClipboardList}
      title="No requirements yet"
      description="Requirements document what the system must do, against your indexed repositories. Index a repo first, then create or generate a requirement from it."
      customAction={consoleUrl ? { label: CHECKLIST_ACTIONS.indexRepo.label, to: consoleUrl(CHECKLIST_ACTIONS.indexRepo.to) } : undefined}
      disabledNote={capabilityDisabled ? {
        message: 'Requirements are disabled for this workspace, so SystemDox won’t ask for any.',
        enableHref: consoleUrl ? consoleUrl('/settings/repo-roles') : undefined,
      } : undefined}
    />
  );

  return (
    // Keyed by docType so switching Requirements ⇄ Design (this component holds
    // the same position in DocViewerPage) remounts the section with a fresh
    // "Find item" query rather than leaking it across pillars.
    <DocListSection
      key={docType}
      title={pillar.title}
      subtitle={pillar.subtitle}
      headerActions={headerActions}
      banner={banner}
      loading={loading || (docType === 'design' && decisions === null)}
      items={items}
      emptyState={emptyState}
      noMatchLabel={`No ${pillar.emptyNoun} match your search.`}
    />
  );
}
